import React, { useState, memo } from "react";
import { Stack, Button } from "@mui/material";

import { NoteItem } from "./NoteItem";

import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import {
  selectProductNotes,
  selectProductTags,
} from "../../../../features/productAnalyzer/productAnalyzer.selector";
import { INote } from "../../../../features/productAnalyzer/productAnalyzer.types";
import { useProductAnalyzer } from "../../../../contexts/productAnalyzerContext";
import { selectTags } from "../../../../features/settings/settings.selector";
import { ITag } from "../../../../features/settings/settings.types";
import {
  createProductTagThunk,
  deleteProductTagThunk,
} from "../../../../features/productAnalyzer/productAnalyzer.thunk";
import { ToggleButton } from "../../../../components";
import TagMenu from "../../../Settings/ProductAnalyzer/TagMenu";

const Notes: React.FC = () => {
  const dispatch = useAppDispatch();
  const { notes } = useAppSelector(selectProductNotes);
  const productTags = useAppSelector(selectProductTags);
  const tags = useAppSelector(selectTags);
  const { asin } = useProductAnalyzer();
  const [isAdding, setIsAdding] = useState<boolean>(false);
  const [isUpdatingTag, setIsUpdatingTag] = useState<string>();
  const [tagMenuEl, setTagMenuEl] = useState<null | HTMLElement>(null);
  const isTagMenuOpened = Boolean(tagMenuEl);

  const handleAddNoteClick = () => {
    setIsAdding(true);
  };

  const handleTagClick = (tag: ITag) => {
    setIsUpdatingTag(tag.id);
    if (productTags.includes(tag.id)) {
      dispatch(deleteProductTagThunk({ asin, tagId: tag.id }))
        .unwrap()
        .finally(() => setIsUpdatingTag(undefined));
    } else {
      dispatch(createProductTagThunk({ asin, tagId: tag.id }))
        .unwrap()
        .finally(() => setIsUpdatingTag(undefined));
    }
  };

  return (
    <Stack gap="4px" padding="4px 4px">
      <Stack direction="row" gap="4px" display="flex" flexWrap="wrap">
        <Button
          variant="contained"
          id="tag-button"
          aria-controls={isTagMenuOpened ? "tag-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={isTagMenuOpened ? "true" : undefined}
          onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
            setTagMenuEl(event.currentTarget);
          }}
        >
          Add Tag
        </Button>
        <Button
          variant="contained"
          onClick={() => handleAddNoteClick()}
          disabled={isAdding}
        >
          Add Note
        </Button>
        {tags.map((tag) => (
          <ToggleButton
            value={tag.id}
            size="medium"
            selected={productTags.includes(tag.id)}
            onClick={() => handleTagClick(tag)}
            disabled={tag.id === isUpdatingTag}
          >
            {tag.caption}
          </ToggleButton>
        ))}
      </Stack>
      {(!!notes.length || isAdding) && (
        <Stack sx={{ gap: "4px" }}>
          {isAdding && (
            <NoteItem asin={asin} onClose={() => setIsAdding(false)} />
          )}
          {notes.map((note: INote, index: number) => (
            <NoteItem key={`note-item-${note.id}`} asin={asin} data={note} />
          ))}
        </Stack>
      )}

      <TagMenu
        open={isTagMenuOpened}
        anchorEl={tagMenuEl}
        tag={null}
        onClose={() => setTagMenuEl(null)}
      />
    </Stack>
  );
};

export default memo(Notes);
